import { computed, Injectable, signal } from '@angular/core';
import {
  AUTH_STATE,
  ENROLL_EVENT_REPORT,
  ENROLLED_USER_SESSION,
  REGISTRATION_ID,
} from 'src/app/@constants/local-storage-key-constants';
import {
  AuthState,
  AuthStatus,
  ResolvedUser,
  UserAuthError,
} from 'src/app/@data-services/user-auth/user-auth.data-model';
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
} from 'src/app/@utils/local-storage-util';

const AUTH_EXPIRATION_MS = 24 * 60 * 60 * 1000; // 24 hours (fallback)

interface PersistedAuthState extends AuthState {
  eventNameIdentifier: string;
  expiresAt?: number; // Cognito token expiry (epoch ms) if available
}

const initialState: AuthState = {
  status: 'idle',
  user: null,
  error: null,
  timestamp: null,
};

const state = {
  authState: signal<AuthState>(initialState),
  eventNameIdentifier: signal<string>(''),
  expiresAt: signal<number | null>(null),
};

@Injectable({
  providedIn: 'root',
})
export class AuthStateService {
  constructor() {
    this._initializeFromStorage();
  }

  public readonly $authState = state.authState.asReadonly();
  public readonly $eventNameIdentifier = state.eventNameIdentifier.asReadonly();

  public readonly $status = computed(() => this.$authState().status);
  public readonly $user = computed(() => this.$authState().user);
  public readonly $error = computed(() => this.$authState().error);

  public readonly $isAuthenticated = computed(
    () => this.$authState().status === 'authenticated'
  );

  public readonly $isAuthenticating = computed(
    () => this.$authState().status === 'authenticating'
  );

  public readonly $userId = computed(
    () => this.$authState().user?.userId ?? null
  );

  public readonly $registrationId = computed(
    () => this.$authState().user?.registrationId ?? null
  );

  public readonly $email = computed(
    () => this.$authState().user?.email ?? null
  );

  setStatus(status: AuthStatus): void {
    state.authState.update((current) => ({
      ...current,
      status,
      timestamp: Date.now(),
    }));
    this._persistToStorage();
  }

  setUser(user: ResolvedUser, tokenExpiresAt?: number): void {
    state.authState.update((current) => ({
      ...current,
      user,
      status: 'authenticated',
      error: null,
      timestamp: Date.now(),
    }));
    if (tokenExpiresAt) {
      state.expiresAt.set(tokenExpiresAt);
    }
    this._persistToStorage();
  }

  setError(error: UserAuthError, status: AuthStatus = 'error'): void {
    state.authState.update((current) => ({
      ...current,
      error,
      status,
      timestamp: Date.now(),
    }));
    this._persistToStorage();
  }

  setEventNameIdentifier(eventNameIdentifier: string): void {
    state.eventNameIdentifier.set(eventNameIdentifier);
  }

  reset(): void {
    state.authState.set(initialState);
    state.eventNameIdentifier.set('');
    state.expiresAt.set(null);
    removeLocalStorageItem(AUTH_STATE);
  }

  isValidForEvent(eventNameIdentifier: string): boolean {
    const currentEvent = state.eventNameIdentifier();
    const authState = state.authState();

    if (currentEvent !== eventNameIdentifier) {
      return false;
    }

    if (authState.status !== 'authenticated') {
      return false;
    }

    return !this._isExpired();
  }

  private _initializeFromStorage(): void {
    const persisted = getLocalStorageItem<PersistedAuthState>(AUTH_STATE);

    if (!persisted) {
      return;
    }

    // Check if the stored state has expired (token expiry or fallback 24h)
    // When expired, clear all related localStorage to prevent stale data
    // from being restored by other services (MixpanelService, ExternalRegistrationStateService)
    if (
      (persisted.expiresAt && Date.now() >= persisted.expiresAt) ||
      this._isExpiredTimestamp(persisted.timestamp)
    ) {
      removeLocalStorageItem(AUTH_STATE);
      removeLocalStorageItem(ENROLL_EVENT_REPORT);
      removeLocalStorageItem(ENROLLED_USER_SESSION);
      removeLocalStorageItem(REGISTRATION_ID);
      return;
    }

    state.authState.set({
      status: persisted.status,
      user: persisted.user,
      error: persisted.error,
      timestamp: persisted.timestamp,
    });
    state.eventNameIdentifier.set(persisted.eventNameIdentifier);
    state.expiresAt.set(persisted.expiresAt ?? null);
  }

  private _persistToStorage(): void {
    const authState = state.authState();
    const eventNameIdentifier = state.eventNameIdentifier();

    const persisted: PersistedAuthState = {
      ...authState,
      eventNameIdentifier,
      expiresAt: state.expiresAt() ?? undefined,
    };

    setLocalStorageItem(AUTH_STATE, persisted);
  }

  private _isExpired(): boolean {
    // Check Cognito token expiry first (more accurate)
    const expiresAt = state.expiresAt();
    if (expiresAt && Date.now() >= expiresAt) {
      return true;
    }
    // Fallback to 24h from last authentication
    const timestamp = state.authState().timestamp;
    return this._isExpiredTimestamp(timestamp);
  }

  private _isExpiredTimestamp(timestamp: number | null): boolean {
    if (!timestamp) {
      return true;
    }
    return Date.now() - timestamp > AUTH_EXPIRATION_MS;
  }
}
